import { RefreshCcw, Music2, Compass, GitBranch, Info, Activity } from "lucide-react";

const confColor = (label) => {
  if (label === "High") return "text-[#39FF14]";
  if (label === "Medium") return "text-[#FFB800]";
  return "text-[#FF6666]";
};

const confDot = (label) => {
  if (label === "High") return "bg-[#39FF14]";
  if (label === "Medium") return "bg-[#FFB800]";
  return "bg-[#FF6666]";
};

export default function ResultCard({ result, onReset }) {
  const {
    song_name,
    detected_key,
    alternate_key_notation,
    relative_key,
    bpm,
    confidence,
    confidence_percent,
    simple_explanation,
    notes,
  } = result;

  return (
    <div
      className="bg-[#0A0A0A] border border-white/5 rounded-3xl p-6 sm:p-8 shadow-2xl flex flex-col gap-6"
      data-testid="result-card"
      aria-live="polite"
    >
      {/* Song name */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-600 font-semibold mb-1.5">
            Detected from
          </div>
          <div
            className="text-sm text-zinc-300 font-body truncate"
            title={song_name}
            data-testid="result-song-name"
          >
            {song_name}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className={`w-2 h-2 rounded-full ${confDot(confidence)} animate-pulse`} />
          <span
            className={`text-[11px] uppercase tracking-[0.18em] font-mono font-semibold ${confColor(confidence)}`}
            data-testid="confidence-value"
          >
            {confidence} · {confidence_percent}%
          </span>
        </div>
      </div>

      {/* Hero key block */}
      <div className="bg-[#0F0F13] border border-white/5 rounded-2xl p-8 flex flex-col items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#FF2D55]/[0.04] to-transparent pointer-events-none" />
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-mono font-semibold mb-3 relative z-10">
          musical key
        </div>
        <div
          className="font-display text-5xl sm:text-6xl font-black tracking-tighter text-white led-glow relative z-10 text-center"
          data-testid="detected-key-value"
        >
          {detected_key}
        </div>
        <div className="mt-4 flex items-center gap-1.5 text-[11px] text-zinc-500 font-body relative z-10">
          <Music2 className="w-3 h-3" />
          <span>Tonal center estimated from chroma analysis</span>
        </div>
      </div>

      {/* Bento: Camelot + Relative + BPM */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-[#0F0F13] border border-white/5 rounded-2xl p-3 flex flex-col gap-1.5">
          <div className="flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
            <Compass className="w-3 h-3 text-[#39FF14]" />
            <span>Camelot</span>
          </div>
          <div
            className="font-mono text-2xl text-[#39FF14] font-bold led-glow-green leading-none"
            data-testid="camelot-value"
          >
            {alternate_key_notation}
          </div>
          <div className="text-[9px] text-zinc-600 font-body">DJ wheel</div>
        </div>

        <div className="bg-[#0F0F13] border border-white/5 rounded-2xl p-3 flex flex-col gap-1.5">
          <div className="flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
            <GitBranch className="w-3 h-3 text-[#FF2D55]" />
            <span>Relative</span>
          </div>
          <div
            className="font-mono text-base text-[#FF2D55] font-semibold leading-tight pt-0.5"
            data-testid="relative-key-value"
          >
            {relative_key}
          </div>
          <div className="text-[9px] text-zinc-600 font-body">Same notes</div>
        </div>

        <div className="bg-[#0F0F13] border border-white/5 rounded-2xl p-3 flex flex-col gap-1.5">
          <div className="flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
            <Activity className="w-3 h-3 text-white" />
            <span>BPM</span>
          </div>
          <div
            className="font-mono text-2xl text-white font-bold leading-none flex items-baseline gap-1"
            data-testid="bpm-value"
          >
            {bpm != null ? bpm : <span className="text-zinc-600 text-base">—</span>}
          </div>
          <div className="text-[9px] text-zinc-600 font-body">Tempo</div>
        </div>
      </div>

      {/* Plain-language explanation */}
      <div className="bg-[#0F0F13] border border-white/5 rounded-2xl p-4">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-2">
          <Info className="w-3 h-3" />
          <span>In plain English</span>
        </div>
        <p
          className="text-sm text-zinc-300 font-body leading-relaxed"
          data-testid="explanation-value"
        >
          {simple_explanation}
        </p>
        {notes && (
          <p
            className="text-[11px] text-zinc-600 font-body mt-3 italic leading-relaxed"
            data-testid="notes-value"
          >
            {notes}
          </p>
        )}
      </div>

      {/* Reset */}
      <button
        onClick={onReset}
        data-testid="reset-button"
        className="w-full bg-white/5 hover:bg-white/10 text-white font-medium px-6 py-3.5 rounded-full transition-colors text-sm flex items-center justify-center gap-2 border border-white/5 font-display"
      >
        <RefreshCcw className="w-4 h-4" />
        Analyze another song
      </button>
    </div>
  );
}
